import React, { useEffect } from "react"
import { Form, InputNumber, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import styles from "./BookingForm.module.css"
const { Text } = Typography
const GuestInputGroup = ({ form, setCounts, disabled, booking }) => {
  const { t } = useTranslation()
  const {
    experienceBookingData,
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)
  const { children_allowed, infants_allowed, passengers_allowed, extra_guests_allowed } = bookingSelections?.tripLength?.price || {}
  const maxGuests = experienceBookingData?.bookingData?.max_guests
  const minGuests = experienceBookingData?.bookingData?.min_guests
  const maxExtraGuest = bookingSelections?.tripLength?.price?.max_extra_guests

  const createValidator = (fieldName, sectionKey) => (_, value) => {
    const values = form.getFieldsValue([sectionKey])
    const sectionValues = values[sectionKey] || {}
    sectionValues[fieldName] = value
    const total = Object.values(sectionValues).reduce((sum, v) => sum + (Number(v) || 0), 0)
    if (fieldName === "adults" && (!value || value < 1) && sectionKey === "main") {
      return Promise.reject(new Error(t("booking.error_messages.adults_required")))
    }
    if (value > 0) {
      if (total > maxGuests && sectionKey === "main") {
        return Promise.reject(new Error(t("booking.error_messages.max_guest_limit", { max: maxGuests })))
      } else if (total < minGuests && sectionKey === "main") {
        return Promise.reject(new Error(t("booking.error_messages.min_guest_limit", { min: minGuests })))
      } else if (total > maxExtraGuest && sectionKey === "extra") {
        return Promise.reject(new Error(t("booking.error_messages.max_guest_limit", { max: maxExtraGuest })))
      } 
    }
    return Promise.resolve()
  }

  const updateCount = (section, key, value) => {
    let newCounts = {
      ...bookingSelections?.counts,
      [section]: {
        ...bookingSelections?.counts?.[section],
        [key]: value
      }
    }
    // If main guest count is being updated
    if (section === "main" && key === "adults") {
      const currentMainTotal = getGuestTotals(newCounts)?.main || 0
      const currentExtraTotal = getGuestTotals(newCounts)?.extra || 0

      // If main guest count is being reduced below max and there are extra guests
      if (currentMainTotal < maxGuests && currentExtraTotal > 0) {
        // Clear extra guest counts
        newCounts = {
          ...newCounts,
          extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
        }
      } else if (currentMainTotal >= maxGuests) {
        newCounts = {
          ...newCounts,
          extra: {
            adults: booking?.extra_adult_count,
            children: booking?.extra_child_count,
            infants: booking?.extra_infant_count,
            passengers: booking?.extra_passenger_count
          }
        }
      }
    }

    setCounts(newCounts)
  }

  useEffect(() => {
    const counts = bookingSelections?.counts
    if (counts) {
      form.setFieldsValue({
        main: counts?.main,
        extra: counts?.extra
      })
    }
  }, [bookingSelections?.counts, form])

  useEffect(() => {
    // Only set if minGuests is defined and main.adults is not already set
    if (minGuests > 0) {
      const currentMain = form.getFieldValue(["main", "adults"])
      if (!currentMain) {
        form.setFieldsValue({ main: { ...form.getFieldValue("main"), adults: minGuests } })
        // Optionally, update counts state if needed
        setCounts({
          ...bookingSelections?.counts,
          main: { ...bookingSelections?.counts?.main, adults: booking?.adult_count || minGuests }
        })
      }
    }
    // Only run when minGuests changes
    // eslint-disable-next-line
  }, [minGuests])

  const getGuestTotals = (counts) => {
    const mainCounts = counts?.main || {}
    const extraCounts = counts?.extra || {}

    const totalMain = Object.values(mainCounts).reduce((sum, val) => sum + (val || 0), 0)
    const totalExtra = Object.values(extraCounts).reduce((sum, val) => sum + (val || 0), 0)

    return { main: totalMain, extra: totalExtra, total: totalExtra + totalMain }
  }

  const renderGuestInputBlock = (sectionLabel, sectionKey, disableAll) => (
    <>
      {sectionLabel && (
        <div style={{ marginBottom: "10px" }}>
          <Text style={{ fontWeight: 600 }}>{sectionLabel}</Text>
        </div>
      )}
      <Space size="small" className={styles.inputGroup}>
        {[
          { label: t("booking.labels.adults"), key: "adults", allowed: true },
          { label: t("booking.labels.children"), key: "children", allowed: children_allowed },
          { label: t("booking.labels.infants"), key: "infants", allowed: infants_allowed },
          { label: t("booking.labels.passengers"), key: "passengers", allowed: passengers_allowed }
        ].map(({ label, key, allowed }) => (
          <Form.Item
            key={sectionKey + key}
            label={label}
            name={[sectionKey, key]} // nested form field path
            rules={[{ validator: createValidator(key, sectionKey) }]}
          >
            <InputNumber
              min={0}
              disabled={(disableAll && bookingSelections?.counts?.[sectionKey]?.[key] === 0) || !allowed || disabled}
              className={styles.inputNumber}
              onChange={(val) => updateCount(sectionKey, key, val)}
            />
          </Form.Item>
        ))}
      </Space>
    </>
  )

  const mainTotals = getGuestTotals(bookingSelections?.counts)?.main
  const disableMainInputs = mainTotals >= maxGuests

  return (
    <>
      {renderGuestInputBlock("", "main", disableMainInputs)}
      {extra_guests_allowed &&
        disableMainInputs &&
        renderGuestInputBlock(
          "Extra Guests",
          "extra",
          getGuestTotals(bookingSelections?.counts)?.extra >= bookingSelections?.tripLength?.price?.max_extra_guests
        )}
    </>
  )
}

GuestInputGroup.propTypes = {
  form: PropTypes.object.isRequired,
  setCounts: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  booking: PropTypes.object.isRequired
}

export default GuestInputGroup
